import React from 'react';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../config/appConfig';
import type { ThemeColors } from '../../contexts/ThemeContext';
import type { SearchFolder, SearchFolderKind } from './useData';
import { getDisplaySummaryStructured } from './getSummarySentence';

const FOLDER_KIND_LABELS: Record<SearchFolderKind, string> = {
  search: 'Rule search',
  inbox: 'Inbox',
  combined: 'Combined inbox',
  sent: 'Sent',
  combinedSent: 'Combined sent',
  mySent: 'My sent',
  combinedMySent: 'Combined my sent',
};

const FOLDER_LABELS: Record<string, string> = {
  '': 'All folders',
  INBOX: 'Inbox',
  DRAFT: 'Draft',
  SENT: 'Sent',
};

function normalizeSearchFolderKind(kind?: string | null): SearchFolderKind {
  const k = (kind ?? 'search').trim().toLowerCase();
  if (k === 'inbox' || k === 'combined' || k === 'sent') return k;
  if (k === 'combinedsent') return 'combinedSent';
  if (k === 'mysent') return 'mySent';
  if (k === 'combinedmysent') return 'combinedMySent';
  return 'search';
}

function parseSearchFolderAccountIds(folder: {
  accountId?: number | null;
  accountIds?: number[] | null;
}): number[] {
  if (folder.accountIds?.length) return folder.accountIds;
  if (folder.accountId != null) return [folder.accountId];
  return [];
}

function formatAccountsValue(
  folder: SearchFolder,
  accountById: Map<number, string>,
): React.ReactNode {
  const ids = parseSearchFolderAccountIds(folder);
  if (ids.length === 0) return 'All accounts';
  if (ids.length === 1) return accountById.get(ids[0]) ?? `Account #${ids[0]}`;

  return (
    <ul className="list-disc list-inside space-y-0.5 text-sm">
      {ids.map((id) => (
        <li key={id}>{accountById.get(id) ?? `Account #${id}`}</li>
      ))}
    </ul>
  );
}

function getFolderLabel(folder: string | null | undefined): string {
  const key = (folder ?? '').trim();
  return FOLDER_LABELS[key] ?? (key || 'All folders');
}

function kindBadgeClass(kind: SearchFolderKind): string {
  switch (kind) {
    case 'inbox':
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300';
    case 'combined':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300';
    case 'sent':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
    case 'combinedSent':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300';
    case 'mySent':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300';
    case 'combinedMySent':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300';
    default:
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300';
  }
}

export const moduleConfig = {
  module: {
    title: 'Manage Smart Folders',
    description: '',
    i18nKey: 'search-folders',
    route: {
      list: '/smart-folders',
      new: '/smart-folders/new',
      detail: (id: number) => `/smart-folders/${id}`,
      edit: (id: number) => `/smart-folders/${id}/edit`,
    },
    api: {
      detail: (id: number) => `/emails/search-folders/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  detail: {
    subtitle: 'Smart Folder Details',
    notFoundTitle: 'Smart folder not found',
    notFoundMessage: 'The smart folder you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Smart Folders',
    defaultTitle: 'Smart Folder',
    getTitle: (item: SearchFolder) => item.name || 'Smart Folder',
  },
  emptyStates: {
    noData: 'No smart folders yet. Create one to quickly filter emails (e.g. by sender or domain).',
    noSearchResults: 'No smart folders match your search.',
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
};

export const getStatusBadges = (item: SearchFolder, _colors?: ThemeColors) => {
  const kind = normalizeSearchFolderKind(item.folderKind);
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${kindBadgeClass(kind)}`}>
      {FOLDER_KIND_LABELS[kind]}
    </span>
  );
};

export const getDetailSections = (
  item: SearchFolder,
  formatDate: (dateString?: string) => string,
  accountById: Map<number, string> = new Map(),
) => {
  const kind = normalizeSearchFolderKind(item.folderKind);
  const summary = getDisplaySummaryStructured(item);
  const isMailboxKind = kind !== 'search';

  const summaryValue = (
    <div className="space-y-2 text-sm">
      <p>{summary.headline}</p>
      {summary.matchLabel && (
        <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
          {summary.matchLabel}
        </span>
      )}
      {summary.conditionLines.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-gray-600 dark:text-gray-400">
          {summary.conditionLines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
      {summary.periodLine && (
        <p className="text-gray-600 dark:text-gray-400">{summary.periodLine}</p>
      )}
    </div>
  );

  const overviewSection = {
    title: 'Overview',
    fields: [
      { label: 'Type', value: FOLDER_KIND_LABELS[kind] },
      { label: 'Summary', value: summaryValue },
      { label: 'Accounts', value: formatAccountsValue(item, accountById) },
      {
        label: 'Mailbox folder',
        value: kind === 'mySent' || kind === 'combinedMySent' ? 'My sent (app)' : getFolderLabel(item.folder),
        condition: isMailboxKind && !!item.folder?.trim(),
      },
      { label: 'Sidebar order', value: String(item.displayOrder) },
      {
        label: 'Owner',
        value: item.ownerName ?? '-',
        condition: !!item.ownerName,
      },
    ],
  };

  const metadataSection = {
    title: 'Metadata',
    fields: [
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ],
  };

  return [overviewSection, metadataSection];
};
